import React, { useEffect, useState } from 'react';
import classNames from '../helper/classNames';
import checkIcon from '../assets/check.svg';
import { Link } from 'react-router-dom';
import { useTrackActivity } from '../hooks/track';

export default function UserProgressBar({
  hasCompletedRecording = false,
  hasViewedContentKit = false,
  showProgressBar = true,
  contentKitStatus,
  contentKitUrl,
}) {
  const [animationStep, setAnimationStep] = useState(1);
  const [localHasViewedContentKit, setLocalViewedContentKit] = useState(false);
  const [localHasCompletedRecording, setLocalHasCompletedRecording] =
    useState(false);

  const firstAnimationTime = 1_000;
  const track = useTrackActivity('UserProgress');

  useEffect(() => {
    setTimeout(() => {
      setAnimationStep(2);
    }, firstAnimationTime);
  }, []);

  useEffect(() => {
    if (hasCompletedRecording) {
      setTimeout(() => {
        setLocalHasCompletedRecording(true);
      }, firstAnimationTime);
    }
  }, [hasCompletedRecording]);

  useEffect(() => {
    if (hasViewedContentKit) {
      setTimeout(() => {
        setLocalViewedContentKit(true);
      }, firstAnimationTime);
    }
  }, [hasViewedContentKit]);

  useEffect(() => {
    if (showProgressBar) {
      track('Progress Bar Shown', {
        hasCompletedRecording,
        hasViewedContentKit,
        contentKitStatus,
      });
    }
  }, [
    showProgressBar,
    hasCompletedRecording,
    hasViewedContentKit,
    contentKitStatus,
    track,
  ]);

  if (!showProgressBar) {
    return null;
  }

  // Determine if the content kit is ready to view
  const isContentKitReady = contentKitStatus === 'COMPLETE';

  // Determine display text for the content kit link
  const contentKitLinkText = isContentKitReady
    ? 'View My Content Kit'
    : 'Preview Sample Content Kit';

  // Determine the link URL - use the actual URL if ready, otherwise use the sample
  const linkUrl =
    isContentKitReady && contentKitUrl
      ? contentKitUrl
      : '/e/HN6p1wfM/contentkit';

  // Check if the kit is still processing
  const isProcessing =
    contentKitStatus === 'PROCESSING' ||
    contentKitStatus === 'WAITING_FOR_CALLBACK' ||
    contentKitStatus === 'WAITING FOR INPUT' ||
    contentKitStatus === 'INCOMPLETE';

  return (
    <div className="w-full mx-auto relative max-w-[1225px]">
      {/* Header text */}
      <div className="bg-[#F2F2F2] p-4 border border-[#B5B5B5] rounded-t-md">
        <h2 className="text-2xl font-bold font-roboto text-[#151C71] text-center">
          Let's get your first video done in under 5 minutes
        </h2>
        <p className="text-base text-center font-roboto font-medium text-[#6B6B6B]">
          You're one step closer to sharing your first video with the world.
          Let's finish strong!
        </p>
      </div>

      {/* Progress steps container */}
      <div className="relative flex items-center justify-around border-x border-b border-[#B5B5B5]  bg-[#FAFAFA] py-6 rounded-b-md">
        {/* Step 1 - Always completed */}
        <div className="flex gap-4 items-center">
          <div
            className={classNames(
              'flex items-center justify-center w-12 h-12 rounded-full z-10',
              animationStep === 1
                ? 'animate-[fadeOutAndShrink_300ms_700ms_both] bg-[#AAAAAA]'
                : 'animate-[fadeInAndGrow_300ms_300ms_both] bg-[#00B57F]'
            )}
          >
            {animationStep === 1 ? (
              <span className="text-white font-bold text-2xl">1</span>
            ) : (
              <img src={checkIcon} alt="Completed" className="w-6 h-6 " />
            )}
          </div>
          {/* Step 1 text */}
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-center">
              <span
                className={classNames(
                  'font-roboto text-xl font-medium text-center whitespace-nowrap',
                  animationStep === 1 ? 'text-[#AAAAAA]' : 'text-[#00B57F]'
                )}
              >
                Create your first session
              </span>
            </div>
          </div>
        </div>

        <span
          className={classNames(
            'left-1/3 absolute -translate-x-full text-black font-poppins text-2xl mt-2',
            animationStep === 1 ? 'text-[#AAAAAA]' : 'text-[#00B57F]'
          )}
        >
          &gt;
        </span>

        {/* Step 2 */}
        <div className="flex gap-4 items-center">
          {/* Step indicator with number or check */}
          <div
            className={classNames(
              'flex items-center justify-center w-12 h-12 rounded-full z-10',
              localHasCompletedRecording
                ? 'animate-[fadeInAndGrow_300ms_300ms_both] bg-[#00B57F]'
                : !hasCompletedRecording
                ? 'bg-[#AAAAAA]'
                : 'animate-[fadeOutAndShrink_300ms_700ms_both] bg-[#AAAAAA]'
            )}
          >
            {localHasCompletedRecording ? (
              <img src={checkIcon} alt="Completed" className="w-6 h-6" />
            ) : (
              <span className="text-white font-bold text-2xl">2</span>
            )}
          </div>
          {/* Step 2 text */}
          <div className="flex gap-1">
            <div className="flex flex-col">
              <span
                className={classNames(
                  'text-xl font-roboto font-medium whitespace-nowrap transition-colors duration-500',
                  localHasCompletedRecording
                    ? 'text-[#00B57F]'
                    : 'text-[#AAAAAA]'
                )}
              >
                Record your session
              </span>
              <a
                href="https://www.zync.ai/lessons/navigating-the-recording-studio"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#8B33F7] font-medium text-sm font-roboto"
                onClick={() => track('Recording Guide Link Clicked')}
              >
                Navigating the Recording Studio
              </a>
            </div>
          </div>
        </div>

        <span
          className={classNames(
            'left-2/3 absolute -translate-x-full text-black font-poppins text-2xl mt-2 transition-colors duration-500',
            localHasCompletedRecording ? 'text-[#00B57F]' : 'text-[#C5C5C5]'
          )}
        >
          &gt;
        </span>

        {/* Step 3 */}
        <div className="flex gap-4 items-center">
          {/* Step indicator with number or check */}
          <div
            className={classNames(
              'flex items-center justify-center w-12 h-12 rounded-full z-10',
              localHasViewedContentKit
                ? 'animate-[fadeInAndGrow_300ms_300ms_both] bg-[#00B57F]'
                : !hasViewedContentKit
                ? 'bg-[#AAAAAA]'
                : 'animate-[fadeOutAndShrink_300ms_700ms_both] bg-[#AAAAAA]'
            )}
          >
            {localHasViewedContentKit ? (
              <img src={checkIcon} alt="Completed" className="w-6 h-6" />
            ) : (
              <span className="text-white font-bold text-2xl">3</span>
            )}
          </div>
          {/* Step 3 text */}
          <div className="flex text-xl flex-col font-roboto">
            <span
              className={classNames(
                'font-medium  whitespace-nowrap transition-colors duration-500',
                localHasViewedContentKit ? 'text-[#00B57F]' : 'text-[#C5C5C5]'
              )}
            >
              Review your Content Kit
            </span>
            <Link
              to={linkUrl}
              className="text-[#8B33F7] font-medium text-sm"
              onClick={() =>
                track('Content Kit Link Clicked', {
                  isReady: isContentKitReady,
                  contentKitStatus,
                })
              }
            >
              {contentKitLinkText}
            </Link>
            {isProcessing && (
              <p className="text-xs text-red">Getting ready...</p>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar filled portion */}
      <div
        className={classNames(
          'absolute left-0 h-[4px] bg-[#00B57F] bottom-0 z-[1] transition-all duration-[1500ms]',
          localHasViewedContentKit
            ? 'w-full'
            : localHasCompletedRecording
            ? 'w-2/3'
            : animationStep === 2
            ? 'w-1/3'
            : 'w-0'
        )}
      />

      {/* Progress bar background (gray line) */}
      <div className="absolute left-0 right-0 h-[2px] bg-gray bottom-0 z-0 rounded-md" />
    </div>
  );
}
